import { Button as AntButton, Modal, Select, Table, TableColumnsType } from "antd";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSurveyQuestions } from "../../../../api/surveyQuestion.api";
import useAxios from "../../../../hooks/useAxios";
import config from "../../../../lib/config";
import { campusHeadOffice } from "../../../../lib/data";
import { getCommonCodeLabel } from "../../../../lib/store";
import { IGetSurveyQuestions, ISurveyQuestionItem } from "../../../../models/surveyQuestion.model";
import { RootState } from "../../../../store/rootReducer";
import Button from "../../../Button";
import Checkbox from "../../../Checkbox";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import Pagination from "../../../Pagination";
import SurveyAnswerModal from "./SurveyAnswerModal";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  questions: ISurveyQuestionItem[];
  setQuestions: (questions: ISurveyQuestionItem[]) => void;
  campusCodes: string[];
}

const SelectQuestionModal: FC<IProps> = ({
  visible,
  setVisible,
  questions,
  setQuestions,
  campusCodes,
}) => {
  const { t } = useTranslation();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0007 = 설문문항 카테고리
  const HP0007 = commonCodes.find(item => item.commonCode === 'HP0007')?.detailCode;  
  const { data: res, isLoading, error, execute } = useAxios<IGetSurveyQuestions>();

  const [surveyCategory, setSurveyCategory] = useState<string>();
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ISurveyQuestionItem[]|undefined>([]);
  const [questionCodes, setQuestionCodes] = useState<number[]>([]);
  const [surveyAnswerVisible, setSurveyAnswerVisible] = useState(false);
  const [questionCode, setQuestionCode] = useState<number>();

  useEffect(() => {
    if (currentCampus) {
      doGetSurveyQuestions();
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data);
    }
  }, [res, isLoading]);

  const doGetSurveyQuestions = () => {
    const params: any = {
      pageNo: 1,
      perPage: 999999999,
      campusCodes,
    };

    if (auth.hqYn === 'Y' && campusCodes.length) {
      params.campusCodes = [...campusCodes, campusHeadOffice];
    }

    execute(getSurveyQuestions(params));
  }

  const onSearch = () => {
    let _data: ISurveyQuestionItem[]|undefined = _.cloneDeep(res?.data);

    if (surveyCategory) {
      _data = _data?.filter(item => item.surveyCategory === surveyCategory);
    }
    if (title) {
      _data = _data?.filter(item => item.title.includes(title));
    }

    setData(_data);
  }

  const onSelect = (code: number) => {
    if (questionCodes.includes(code)) {
      setQuestionCodes(questionCodes.filter(item => item !== code));
    } else {
      setQuestionCodes([...questionCodes, code]);
    }
  }

  const onClose = () => {
    setQuestionCodes([]);
    setVisible(false);
  }

  const onAddQuestions = () => {
    const _questions = [...questions];
    questionCodes.forEach((code, index) => {
      const question = res?.data.find(item => item.questionCode === code);
      if (question) {
        _questions.push({
          ...question,
          seq: questions.length + index + 1,
        });
      }
    });
    setQuestions(_questions);
    onClose();
  }

  const columns: TableColumnsType<ISurveyQuestionItem> = [
    {
      title: t('common.column.category'),
      dataIndex: 'surveyCategory',
      key: 'surveyCategory',
      align: 'center',
      render: value => getCommonCodeLabel('HP0007', value, 'ko'),
    },
    {
      title: t('homepage.survey.column12'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
    },
    {
      title: t('homepage.survey.column13'),
      dataIndex: 'questionCode',
      key: 'questionCode',
      align: 'center',
      render: (value, record) => (
        <Checkbox
          checked={questionCodes.includes(value)}
          onClickCapture={e => {
            e.stopPropagation();
            onSelect(value)
          }}
          text=""
        />
      )
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <Modal
        title={t('homepage.survey.button2')}
        visible={visible}
        onCancel={onClose}
        width={750}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onAddQuestions}
            >
              {t('common.button.add')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={onClose}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="common-search">
          <div className="search-area">
            <ul className="search-bx" style={{justifyContent:'right'}}>
            <li>
            <Select
              className="w-150p"
              placeholder={t('common.column.category')}
              value={surveyCategory}
              onChange={value => setSurveyCategory(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {HP0007?.map((item, index) => (
                <Select.Option
                  key={index}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
            
          <input
            type="text"
            className="form-control w-240p"
            placeholder={t('homepage.survey.placeholder1')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
          <div className="comm-btn__area">
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
          </li>
          </ul>
          </div>
        </div>
        <div className='comm-table__area mt-3'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.miniPageSize,
            }}
            onRow={record => {
              return {
                onClick: () => {
                  setQuestionCode(record.questionCode);
                  setSurveyAnswerVisible(true);
                },
              };
            }}
            rowClassName="pointer"
            rowKey="noticeCode"
          />          
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              setPage={setPage}
              mini
            />
          </div>
        </div>

      </Modal>

      {surveyAnswerVisible &&
        <SurveyAnswerModal
          visible={surveyAnswerVisible}
          setVisible={setSurveyAnswerVisible}
          questionCode={questionCode}
        />
      }
    </>
  );
}

export default SelectQuestionModal;
